import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Users, Clock, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { RiderLandingFormData, ApiQuoteRequest } from "@/types/rider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { popularVenues } from "@/utils/venueData";
import { getQuote } from "@/services/riderService";
import { MobileOptionsDrawer } from "./MobileOptionsDrawer";
import { LocationMapPreview } from "./LocationMapPreview";
interface RiderLandingFormProps {
  onSubmit: (formData: RiderLandingFormData, price: number) => void;
}
interface PlaceResult {
  place_id: string;
  description: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  geocode?: Array<{
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
  }>;
}
export const RiderLandingForm = ({
  onSubmit
}: RiderLandingFormProps) => {
  const [destination, setDestination] = useState("");
  const [capacity, setCapacity] = useState(10);
  const [duration, setDuration] = useState(3);
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [placeResults, setPlaceResults] = useState<PlaceResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [showingFallbackResults, setShowingFallbackResults] = useState(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [isCapacitySelectOpen, setIsCapacitySelectOpen] = useState(false);
  const [isDurationSelectOpen, setIsDurationSelectOpen] = useState(false);
  const [isTripTypeSelectOpen, setIsTripTypeSelectOpen] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState<{latitude: number, longitude: number} | null>(null);
  const searchTimeoutRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const {
    toast
  } = useToast();

  // Close mobile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && showMobileDropdown && mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node) && inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowMobileDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, showMobileDropdown]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!destination) {
      toast({
        title: "Missing destination",
        description: "Please enter a destination",
        variant: "destructive"
      });
      return;
    }
    if (!date) {
      toast({
        title: "Missing date",
        description: "Please select a date for your event",
        variant: "destructive"
      });
      return;
    }
    setIsLoadingQuote(true);
    try {
      console.log("=== FORM SUBMISSION DEBUG ===");
      console.log("Form data before API request:");
      console.log("- destination:", destination);
      console.log("- selectedPlace:", selectedPlace);
      console.log("- capacity:", capacity);
      console.log("- duration:", duration);
      console.log("- isRoundTrip:", isRoundTrip);
      console.log("- date:", date);
      console.log("- date type:", typeof date);
      const apiQuoteRequest: ApiQuoteRequest = {
        date: date ? format(date, "yyyy-MM-dd") : null,
        is_round_trip: isRoundTrip,
        passenger_count: capacity,
        duration_hours: duration,
        address: selectedPlace ? selectedPlace.description : destination
      };
      console.log("=== API REQUEST PREPARATION ===");
      console.log("Formatted API request:", apiQuoteRequest);
      if (date) {
        console.log("Date formatting debug:");
        console.log("- Original date object:", date);
        console.log("- Formatted date (yyyy-MM-dd):", format(date, "yyyy-MM-dd"));
        console.log("- Previous format would have been:", format(date, "MMMM d yyyy"));
      }
      console.log("Calling getQuote API...");
      const quoteResponse = await getQuote(apiQuoteRequest);
      console.log("Received quote response:", quoteResponse);
      console.log("Calling onSubmit with price:", quoteResponse.price);
      onSubmit({
        destination: selectedPlace ? selectedPlace.description : destination,
        placeId: selectedPlace?.place_id,
        capacity,
        duration,
        isRoundTrip,
        date,
        coordinates: selectedCoordinates || undefined
      }, quoteResponse.price);
    } catch (error) {
      console.error("=== FORM SUBMISSION ERROR ===");
      console.error("Error in handleSubmit:", error);
      toast({
        title: "Failed to get quote",
        description: "There was an error getting a price quote. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingQuote(false);
    }
  };
  const handleMobileFormSubmit = () => {
    if (!destination) {
      toast({
        title: "Missing destination",
        description: "Please enter a destination",
        variant: "destructive"
      });
      return;
    }
    if (isMobile) {
      setIsMobileDrawerOpen(true);
    }
  };
  const handleMobileDrawerDone = async () => {
    setIsMobileDrawerOpen(false);
    if (!date) {
      toast({
        title: "Missing date",
        description: "Please select a date for your event",
        variant: "destructive"
      });
      return;
    }
    setIsLoadingQuote(true);
    try {
      const apiQuoteRequest: ApiQuoteRequest = {
        date: date ? format(date, "yyyy-MM-dd") : null,
        is_round_trip: isRoundTrip,
        passenger_count: capacity,
        duration_hours: duration,
        address: selectedPlace ? selectedPlace.description : destination
      };
      const quoteResponse = await getQuote(apiQuoteRequest);
      onSubmit({
        destination: selectedPlace ? selectedPlace.description : destination,
        placeId: selectedPlace?.place_id,
        capacity,
        duration,
        isRoundTrip,
        date,
        coordinates: selectedCoordinates || undefined
      }, quoteResponse.price);
    } catch (error) {
      console.error("Error in handleMobileDrawerDone:", error);
      toast({
        title: "Failed to get quote",
        description: "There was an error getting a price quote. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingQuote(false);
    }
  };
  const handleDestinationChange = (value: string) => {
    setDestination(value);
    setSelectedPlace(null);
    setShowingFallbackResults(false);

    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
    }
    if (value.length >= 3) {
      setIsSearching(true);
      if (isMobile) {
        setShowMobileDropdown(true);
      } else {
        setIsPopoverOpen(true);
      }

      // Debounce API call
      searchTimeoutRef.current = window.setTimeout(() => {
        fetchPlaces(value);
      }, 300);
    } else {
      setPlaceResults([]);
      setShowMobileDropdown(false);
      setIsPopoverOpen(false);
    }
  };
  const fetchPlaces = async (query: string) => {
    console.log(`Searching for places with query: "${query}"`);
    try {
      const response = await fetch(`https://api.discoveryconciergeservices.com/api/v1/places/search/?q=${encodeURIComponent(query)}`);
      console.log(`API response status: ${response.status}`);
      if (response.ok) {
        const data = await response.json();
        console.log("Places API response:", data);
        if (data && Array.isArray(data)) {
          // Limit to top 3 results
          const limitedResults = data.slice(0, 3).map((item: any) => ({
            place_id: item.place_id || item.id || `manual-${Math.random().toString(36).substring(7)}`,
            description: item.description || item.name || item.formatted_address || item,
            geocode: item.geocode // Include geocode data if available
          }));
          setPlaceResults(limitedResults);
          console.log("Processed API results (top 3):", limitedResults);
        } else if (data.results && Array.isArray(data.results)) {
          // Limit to top 3 results
          const limitedResults = data.results.slice(0, 3).map((item: any) => ({
            place_id: item.place_id || item.id || `manual-${Math.random().toString(36).substring(7)}`,
            description: item.description || item.name || item.formatted_address || item,
            geocode: item.geocode // Include geocode data if available
          }));
          setPlaceResults(limitedResults);
          console.log("Processed API results (top 3):", limitedResults);
        } else {
          console.log("No results array found in API response, using fallback data");
          const fallbackResults = getFallbackResults(query);
          if (fallbackResults.length > 0) {
            setPlaceResults(fallbackResults);
            setShowingFallbackResults(true);
            console.log("Using fallback results:", fallbackResults);
          } else {
            setPlaceResults([]);
            toast({
              title: "No results found",
              description: "Try a different search term or select a destination manually.",
              duration: 3000
            });
          }
        }
      } else {
        console.error("Failed to fetch places:", response.statusText);
        const fallbackResults = getFallbackResults(query);
        if (fallbackResults.length > 0) {
          setPlaceResults(fallbackResults);
          setShowingFallbackResults(true);
          console.log("API call failed, using fallback results:", fallbackResults);
        } else {
          setPlaceResults([]);
          toast({
            title: "Search failed",
            description: "There was an error searching for destinations.",
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error("Error fetching places:", error);
      const fallbackResults = getFallbackResults(query);
      if (fallbackResults.length > 0) {
        setPlaceResults(fallbackResults);
        setShowingFallbackResults(true);
        console.log("API call error, using fallback results:", fallbackResults);
      } else {
        setPlaceResults([]);
        toast({
          title: "Search error",
          description: "There was a problem connecting to the search service.",
          variant: "destructive"
        });
      }
    } finally {
      setIsSearching(false);
    }
  };
  const getFallbackResults = (query: string): PlaceResult[] => {
    const lowercaseQuery = query.toLowerCase();
    // Limit fallback results to top 3 as well
    return popularVenues.filter(venue => venue.name.toLowerCase().includes(lowercaseQuery) || venue.location.toLowerCase().includes(lowercaseQuery)).slice(0, 3).map(venue => ({
      place_id: venue.id,
      description: `${venue.name}, ${venue.location}`,
      coordinates: venue.coordinates
    }));
  };
  const selectPlace = async (place: PlaceResult) => {
    console.log("=== PLACE SELECTION DEBUG ===");
    console.log("Selected place:", place);
    console.log("Place has coordinates:", !!place.coordinates);
    
    setSelectedPlace(place);
    setDestination(place.description);
    setPlaceResults([]);
    setShowMobileDropdown(false);
    setIsPopoverOpen(false);
    
    // Check if coordinates are available directly from search results
    if (place.coordinates) {
      console.log("Using provided coordinates from place object:", place.coordinates);
      setSelectedCoordinates(place.coordinates);
    } else if (place.geocode?.[0]?.geometry?.location) {
      console.log("Using geometry coordinates from search results:", place.geocode[0].geometry.location);
      setSelectedCoordinates({
        latitude: place.geocode[0].geometry.location.lat,
        longitude: place.geocode[0].geometry.location.lng
      });
    } else {
      console.log("No coordinates in search results, attempting to fetch from details API...");
      // For places from API without coordinates, try to get from details endpoint
      try {
        const geocodeResult = await geocodeAddress(place.description);
        if (geocodeResult) {
          console.log("Geocoded coordinates from details API:", geocodeResult);
          setSelectedCoordinates(geocodeResult);
        } else {
          console.log("Details API also failed, using default coordinates");
          // Fallback to a default location (Los Angeles downtown) so map still shows
          setSelectedCoordinates({ latitude: 34.0522, longitude: -118.2437 });
        }
      } catch (error) {
        console.error("Error fetching coordinates from details API:", error);
        // Fallback to a default location so map still shows
        setSelectedCoordinates({ latitude: 34.0522, longitude: -118.2437 });
      }
    }

    // Clear any pending timeout to prevent further API calls
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
    }
    if (showingFallbackResults) {
      toast({
        title: "Using local venue data",
        description: "This destination was found in our popular venues database."
      });
    }
  };
  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    setIsDatePopoverOpen(false);
  };
  const handleCapacityChange = (value: string) => {
    setCapacity(parseInt(value));
    setIsCapacitySelectOpen(false);
  };
  const handleDurationChange = (value: string) => {
    setDuration(parseInt(value));
    setIsDurationSelectOpen(false);
  };
  const handleTripTypeChange = (value: string) => {
    setIsRoundTrip(value === "round-trip");
    setIsTripTypeSelectOpen(false);
  };
  // Simple geocoding function using a free geocoding service
  const geocodeAddress = async (address: string): Promise<{latitude: number, longitude: number} | null> => {
    try {
      // Test places/details API endpoint for geometry data
      console.log("Attempting to fetch details for place_id:", selectedPlace?.place_id);
      const response = await fetch(`https://api.discoveryconciergeservices.com/api/v1/places/details/?place_id=${selectedPlace?.place_id}`);
      if (response.ok) {
        const data = await response.json();
        console.log("Places API details response:", data);
        console.log("Testing details geometry data:", {
          hasGeometry: !!data.geometry,
          hasGeocode: !!(data.geocode && data.geocode[0]?.geometry),
          geometry: data.geometry,
          geocode: data.geocode,
          fullResponse: data
        });
        
        // Check for geometry in the geocode array first
        if (data.geocode && data.geocode[0]?.geometry?.location) {
          return {
            latitude: data.geocode[0].geometry.location.lat,
            longitude: data.geocode[0].geometry.location.lng
          };
        }
        
        // Fallback to direct geometry field
        if (data.geometry?.location) {
          return {
            latitude: data.geometry.location.lat,
            longitude: data.geometry.location.lng
          };
        }
      } else {
        console.log("Places details API response not OK:", response.status, response.statusText);
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
    }
    return null;
  };

  const capacityOptions = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 25, 30, 40, 50];
  const durationOptions = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
  const isFormValid = destination.trim() !== "" && date !== undefined;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.5,
    delay: 0.2
  }} className="w-full max-w-md">
      {isMobile ?
    // Mobile Layout with dropdown menu
    <div className="space-y-6">
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/30 relative">
            <Label className="text-white text-[14px] mb-2 block text-left">
              Enter Destination
            </Label>
            <div className="relative">
              <div className="flex items-center bg-white rounded-md overflow-hidden">
                <div className="pl-3">
                  <MapPin className="text-gray-500" size={20} />
                </div>
                <Input ref={inputRef} type="text" placeholder="Where's The Party?" className="border-none focus-visible:ring-0 focus-visible:ring-white focus-visible:ring-offset-white text-black bg-white" value={destination} onChange={e => handleDestinationChange(e.target.value)} onFocus={() => {
              if (placeResults.length > 0 && !selectedPlace) {
                setShowMobileDropdown(true);
              }
            }} />
              </div>
              
              {/* Mobile dropdown menu */}
              {showMobileDropdown && <div ref={mobileDropdownRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                  {isSearching ? <div className="p-4 text-center text-gray-500 text-sm">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        <span>Searching...</span>
                      </div>
                    </div> : placeResults.length > 0 ? <div>
                      {placeResults.map((place, index) => <div key={place.place_id} onClick={() => selectPlace(place)} className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 ${index !== placeResults.length - 1 ? 'border-b border-gray-100' : ''}`}>
                          <MapPin className="mr-3 h-4 w-4 text-gray-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-black block truncate">{place.description}</span>
                          </div>
                          {showingFallbackResults && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex-shrink-0">
                              Popular
                            </span>}
                        </div>)}
                    </div> : <div className="p-4 text-center text-gray-500">
                      <div className="text-sm">No results found.</div>
                      <div className="text-xs mt-2 text-gray-400">
                        Try searching for venues like "Crypto.com Arena" or "Hollywood Bowl"
                      </div>
                    </div>}
                </div>}
            </div>
          </div>

          {/* Mobile Map Preview - displayed after destination selection, before Find A Party Bus button */}
          {selectedPlace && selectedCoordinates && (
            <div className="px-4">
              <LocationMapPreview
                locationName={selectedPlace.description.split(',')[0]} // Get just the venue name
                latitude={selectedCoordinates.latitude}
                longitude={selectedCoordinates.longitude}
                className="w-full"
              />
            </div>
          )}
          
          {/* Debug info - remove this later */}
          {selectedPlace && !selectedCoordinates && (
            <div className="px-4 text-white text-xs bg-red-500/20 p-2 rounded">
              DEBUG: Selected place but no coordinates: {selectedPlace.description}
            </div>
          )}

          <div className="flex justify-center">
            <Button onClick={handleMobileFormSubmit} className="rounded-full gradient-bg text-black font-semibold px-10 py-6 text-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoadingQuote || !destination.trim()}>
              {isLoadingQuote ? <>
                  <span className="mr-2">Calculating Price...</span>
                  <div className="h-4 w-4 rounded-full border-2 border-black border-t-transparent animate-spin" />
                </> : "Find A Party Bus"}
            </Button>
          </div>

          <MobileOptionsDrawer isOpen={isMobileDrawerOpen} onClose={() => setIsMobileDrawerOpen(false)} capacity={capacity} setCapacity={setCapacity} duration={duration} setDuration={setDuration} isRoundTrip={isRoundTrip} setIsRoundTrip={setIsRoundTrip} date={date} setDate={setDate} onDone={handleMobileDrawerDone} />
        </div> : <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-3 bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/30 relative">
              <Label className="text-white text-[10px] mb-2 block text-left">
                Enter Destination
              </Label>
              <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                  <div className="flex items-center bg-white rounded-md overflow-hidden">
                    <div className="pl-3">
                      <MapPin className="text-gray-500" size={20} />
                    </div>
                    <Input ref={inputRef} type="text" placeholder="Where's The Party?" className="border-none focus-visible:ring-0 focus-visible:ring-white focus-visible:ring-offset-white text-black bg-white" value={destination} onChange={e => handleDestinationChange(e.target.value)} onFocus={() => {
                  if (placeResults.length > 0 && !selectedPlace) {
                    setIsPopoverOpen(true);
                  }
                }} />
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0 bg-white border border-gray-200 shadow-lg z-50" align="start" onOpenAutoFocus={e => {
              e.preventDefault();
            }}>
                  <div className="max-h-60 overflow-y-auto">
                    {isSearching ? <div className="p-4 text-center text-gray-500">Searching...</div> : placeResults.length > 0 ? <div className="py-2">
                        {placeResults.map(place => <div key={place.place_id} onClick={() => selectPlace(place)} className="flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100">
                            <MapPin className="mr-2 h-4 w-4 text-gray-500" />
                            <span className="text-sm text-black">{place.description}</span>
                            {showingFallbackResults && <span className="ml-2 text-xs bg-gray-100 px-1 rounded text-black">Popular</span>}
                          </div>)}
                      </div> : <div className="p-4 text-center text-gray-500">
                        <div>No results found.</div>
                        <div className="py-2 px-2 text-xs text-gray-500">
                          Try searching for a popular venue like "Crypto.com Arena" or "Hollywood Bowl"
                        </div>
                      </div>}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Date Selector */}
            <div className="col-span-1 bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/30">
              <Label className="text-white text-[8px] md:text-[10px] mb-2 block text-center whitespace-nowrap">
                Enter Date
              </Label>
              <Popover open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
                <PopoverTrigger asChild>
                  <div className="flex items-center justify-center bg-white rounded-md overflow-hidden cursor-pointer h-10">
                    <Calendar className="text-gray-500" size={20} />
                    {date && <span className="text-black ml-2 text-xs">{format(date, "MMM dd")}</span>}
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <CalendarComponent mode="single" selected={date} onSelect={handleDateSelect} initialFocus disabled={date => date < new Date()} className={cn("bg-white pointer-events-auto")} />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 flex flex-col items-center border border-white/30">
              <Label htmlFor="capacity" className="text-white mb-2 text-center">
                # of People
              </Label>
              <div className="flex items-center justify-center w-full">
                <div className="flex items-center justify-center">
                  <Users className="text-white mr-2" size={20} />
                  <Select open={isCapacitySelectOpen} onOpenChange={setIsCapacitySelectOpen} value={capacity.toString()} onValueChange={handleCapacityChange}>
                    <SelectTrigger className="w-full bg-white/20 border-none text-white">
                      <SelectValue placeholder="10" />
                    </SelectTrigger>
                    <SelectContent>
                      {capacityOptions.map(option => <SelectItem key={option} value={option.toString()}>
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 flex flex-col items-center border border-white/30">
              <Label htmlFor="duration" className="text-white mb-2 text-center text-xs whitespace-nowrap">
                Trip Duration
              </Label>
              <div className="flex items-center justify-center w-full">
                <Clock className="text-white mr-1 flex-shrink-0" size={20} />
                <Select open={isDurationSelectOpen} onOpenChange={setIsDurationSelectOpen} value={duration.toString()} onValueChange={handleDurationChange}>
                  <SelectTrigger className="w-full bg-white/20 border-none text-white text-xs">
                    <SelectValue placeholder="3" />
                  </SelectTrigger>
                  <SelectContent>
                    {durationOptions.map(option => <SelectItem key={option} value={option.toString()}>
                        {option} hrs
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 flex flex-col items-center border border-white/30">
              <Label className="text-white mb-2 text-center">
                Trip Type
              </Label>
              <div className="flex items-center justify-center w-full">
                <Select open={isTripTypeSelectOpen} onOpenChange={setIsTripTypeSelectOpen} value={isRoundTrip ? "round-trip" : "one-way"} onValueChange={handleTripTypeChange}>
                  <SelectTrigger className="w-full bg-white/20 border-none text-white text-xs">
                    <SelectValue placeholder="Select trip type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-way">One Way</SelectItem>
                    <SelectItem value="round-trip">Round Trip</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Desktop Map Preview - displayed after selectors, before Find A Party Bus button */}
          {selectedPlace && selectedCoordinates && (
            <div className="mt-4">
              <LocationMapPreview
                locationName={selectedPlace.description.split(',')[0]} // Get just the venue name
                latitude={selectedCoordinates.latitude}
                longitude={selectedCoordinates.longitude}
                className="w-full max-w-md mx-auto"
              />
            </div>
          )}
          
          {/* Debug info - remove this later */}
          {selectedPlace && !selectedCoordinates && (
            <div className="mt-4 text-white text-xs bg-red-500/20 p-2 rounded max-w-md mx-auto">
              DEBUG: Selected place but no coordinates: {selectedPlace.description}
            </div>
          )}

          <div className="flex justify-center mt-8">
            <Button type="submit" className="rounded-full gradient-bg text-black font-semibold px-10 py-6 text-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoadingQuote || !isFormValid}>
              {isLoadingQuote ? <>
                  <span className="mr-2">Calculating Price...</span>
                  <div className="h-4 w-4 rounded-full border-2 border-black border-t-transparent animate-spin" />
                </> : "Find A Party Bus"}
            </Button>
          </div>
        </form>}
    </motion.div>;
};